import { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import classnames from 'classnames';
import css from 'styled-jsx/css';

import { baseUrlOf, redirect, fetchOpts } from '../src/lib/ssr';
import user from '../src/services/api/user';
import subscriptionApi from '../src/services/api/subscriptions';
import countriesApi from '../src/services/api/countries';

import { colors, dimensions, fonts } from '../src/components/common/Theme';
import UserProfile from '../src/components/profile/UserProfile';
import Header from '../src/components/common/Header';
import Footer from '../src/components/common/Footer';
import Button from '../src/components/common/elements/Button';
import CancelSubscription from '../src/components/profile/CancelSubscription';
import SupportForm from '../src/components/common/elements/SupportForm';
import CountryChange from '../src/components/profile/CountryChange';
import Nav from '../src/components/profile/Nav';
import MainContainer from '../src/components/common/elements/MainContainer';
import Subscriptions, { STATUS } from '../src/components/profile/Subscriptions';

import { HREF as SUBSCRIBE_HREF } from './souscrire';
import { HREF as SIMULATION_HREF } from './assurance/simulation';

const HREF = '/profile';
const PROFILE_HREF = HREF;

const componentStyles = css`
  .wrapper {
    display: flex;
    flex-direction: column;
  }

  .col-right {
    margin-top: 40px;
    flex-grow: 1;
  }

  .simulator,
  .new-trip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
  }

  .simulator {
    padding: 40px 64px;
  }

  .new-trip-container {
    margin: 24px;
  }

  .simulator-text,
  .new-trip-text {
    margin-bottom: 20px;
  }

  .simulator-text {
    font-size: 0.875rem;
    line-height: 1.3;
  }

  .simulator-text-main {
    display: block;
    font-size: 1.5rem;
    font-family: ${fonts.primaryFont};
  }

  .good-to-know-title {
    margin: 24px 0 8px;
    color: ${colors.ywgPrimary};
  }

  .good-to-know-title::before {
    margin-right: 5px;
    content: '👌';
  }

  .good-to-know-text {
    font-size: 0.875rem;
  }

  @media (min-width: ${dimensions.ywgProfileMaxWidth}) {
    .wrapper {
      flex-direction: row;
    }

    .col-left {
      margin-right: 64px;
      width: 25%;
    }

    .nav {
      flex-wrap: no-wrap;
    }

    .col-right {
      margin-top: 0;
    }

    .simulator,
    .new-trip {
      flex-direction: row;
    }

    .simulator-text,
    .new-trip-text {
      margin-bottom: 0;
    }

    .good-to-know-title::before {
      display: block;
      margin: 0 0 16px;
    }
  }

  .text-special {
    color: ${colors.ywgPrimary};
  }
`;

const changeCountryDynStyles = css.resolve`
  .action__button {
    position: relative;
    width: 100%;
  }
  .action__button::before {
    content: '';
    width: 1px;
    height: 1px;
    transition: z-index 0.1s ease, background-color 0.2s ease,
      transform 0.4s ease;
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    top: 0;
    height: 100%;
    border-radius: 50%;
    z-index: -1;
  }
  .button {
    margin: 0;
  }
`;

const subscriptionsStyles = css.resolve`
  .title {
    margin: 0 0 24px;
    font-size: 1.5rem;
  }

  @media (min-width: ${dimensions.ywgProfileMaxWidth}) {
    .title {
      font-size: 2.25rem;
    }
  }
`;

const buttonStyles = css.resolve`
  .button {
    margin: 0;
  }

  .new-trip-button {
    flex-shrink: 0;
  }

  @media (min-width: ${dimensions.ywgProfileMaxWidth}) {
    .new-trip-button {
      margin-left: 24px;
    }
  }
`;

const CountryChangeButton = ({ sub, className, children, onClick }) => {
  if (sub.status === STATUS.ACTIVE) {
    return (
      <li className={className}>
        <Button
          onClick={onClick}
          className={classnames(
            'action__button',
            changeCountryDynStyles.className
          )}
        >
          {children}
        </Button>
        {changeCountryDynStyles.styles}
      </li>
    );
  } else {
    return null;
  }
};

const stopSubscriptionButtonStyles = css`
  .tertiary-button {
    padding: 10px 5px;
    font-size: 0.875rem;
    text-decoration: underline;
    border: none;
    background: none;
  }

  .tertiary-button:hover,
  .tertiary-button:active,
  .tertiary-button:focus {
    text-decoration: none;
  }
`;

const StopSubscriptionButton = ({ onClick }) => {
  return (
    <>
      <button className="tertiary-button" onClick={onClick}>
        Arrêter cette assurance
      </button>
      <style jsx>{stopSubscriptionButtonStyles}</style>
    </>
  );
};

const ActionButton = ({
  action,
  children,
  subscription,
  className,
  ...props
}) => {
  const href =
    action === 'confirm'
      ? `/subscriptions/confirm?id=${subscription.id}`
      : null;
  const button = (
    <Button
      href={href}
      className={classnames('action__button', changeCountryDynStyles.className)}
      {...props}
    >
      {children}
    </Button>
  );
  return (
    <li className={className}>
      {href ? <Link href={href}>{button}</Link> : button}
    </li>
  );
};

const Index = ({ subscriptions, profile }) => {
  const [drafts, setDrafts] = useState(
    subscriptions.filter(({ status }) => status === STATUS.DRAFT)
  );
  const [inprogress, setInprogress] = useState(
    subscriptions.filter(({ status }) => status === STATUS.ACTIVE)
  );
  const [incoming, setIncoming] = useState(
    subscriptions.filter(({ status }) => status === STATUS.CONFIRMED)
  );
  const [toCancel, setToCancel] = useState(null);

  const [
    countryChangeSubscriptionId,
    setCountryChangeSubscriptionId
  ] = useState(null);

  const handleInProgressCountryChange = subscription => {
    const inprogressUpdated = inprogress.map(unchanged => {
      return unchanged.id === subscription.id ? subscription : unchanged;
    });
    setInprogress(inprogressUpdated);
  };

  const removeSubscription = subscription => {
    const filterAndUpdate = (datas, update) => {
      const filteredSubscriptions = datas.filter(data => {
        return data.id !== subscription.id;
      });
      update(filteredSubscriptions);
    };
    switch (subscription.status) {
      case STATUS.DRAFT:
        filterAndUpdate(drafts, setDrafts);
        break;
      case STATUS.ACTIVE:
        filterAndUpdate(inprogress, setInprogress);
        break;
      case STATUS.CONFIRMED:
        filterAndUpdate(incoming, setIncoming);
        break;
    }
  };

  const openOverlay = id => {
    setCountryChangeSubscriptionId(id);
  };
  const onClose = () => {
    setCountryChangeSubscriptionId(null);
  };
  const [countries, setCountries] = useState(null);
  useEffect(() => {
    (async () => {
      if (countries === null) {
        const countries = await countriesApi.get();
        setCountries(countries);
      }
    })();
  }, [countries]);

  return (
    <>
      <Head>
        <title key="title">YUPWEGO - Profil</title>
      </Head>
      <Header profile={profile} />
      <MainContainer>
        <h1 className="sr-only">Mon profil YUPWEGO</h1>
        <Nav current={HREF} />
        <div className="wrapper">
          <div className="col-left">
            <UserProfile profile={profile} badge />
            <h2 className="good-to-know-title">Bon à savoir</h2>
            <p className="good-to-know-text">
              Lorsque vous visitez 2 pays dans la même journée, pensez à bien
              activer votre assurance pour chacun.
              <br />
              <strong>
                Vous ne paierez que l’assurance pour le pays qui nécessite
                l’assurance la plus élevée.
              </strong>
            </p>
          </div>
          <div className="col-right">
            <Subscriptions
              className={classnames('title', subscriptionsStyles.className)}
              subs={drafts}
              status={STATUS.DRAFT}
            >
              {(actionClassName, subscription) => (
                <>
                  <CountryChangeButton
                    className={actionClassName}
                    sub={subscription}
                  >
                    Changement de plan 🗺
                  </CountryChangeButton>
                  <ActionButton
                    className={actionClassName}
                    subscription={subscription}
                    action="confirm"
                  >
                    Confirmer
                  </ActionButton>
                  <StopSubscriptionButton
                    onClick={() => setToCancel(subscription)}
                  />
                </>
              )}
            </Subscriptions>
            <Subscriptions
              className={classnames('title', subscriptionsStyles.className)}
              subs={inprogress}
              status={STATUS.ACTIVE}
            >
              {(actionClassName, subscription) => (
                <>
                  <CountryChangeButton
                    className={actionClassName}
                    sub={subscription}
                    onClick={() => openOverlay(subscription.id)}
                  >
                    Changer de pays 🌍
                  </CountryChangeButton>
                  <StopSubscriptionButton
                    onClick={() => setToCancel(subscription)}
                  />
                </>
              )}
            </Subscriptions>
            <Subscriptions
              className={classnames('title', subscriptionsStyles.className)}
              subs={incoming}
              status={STATUS.CONFIRMED}
            >
              {(actionClassName, subscription) => (
                <>
                  <CountryChangeButton
                    className={actionClassName}
                    sub={subscription}
                  >
                    Changement de plan
                  </CountryChangeButton>
                  <StopSubscriptionButton
                    onClick={() => setToCancel(subscription)}
                  />
                </>
              )}
            </Subscriptions>
            <h2 className={classnames(subscriptionsStyles.className, 'title')}>
              Nouveau départ&nbsp;!
            </h2>
            <div className="simulator">
              <p className="simulator-text">
                <span className="simulator-text-main">
                  <span className="text-special">Simulez</span> votre trip
                </span>
                et estimez le coût de votre assurance Yupwego
              </p>
              <Link href={SIMULATION_HREF}>
                <Button
                  href={SIMULATION_HREF}
                  className={buttonStyles.className}
                  secondary
                >
                  Accéder au simulateur 💶
                </Button>
              </Link>
            </div>
            <div className="new-trip-container">
              <h3>Nouveau voyage</h3>
              <div className="new-trip">
                <p className="new-trip-text">
                  Prêt à partir pour de nouvelles aventures ?
                </p>
                <Link href={SUBSCRIBE_HREF}>
                  <Button
                    href={SUBSCRIBE_HREF}
                    className={classnames(
                      buttonStyles.className,
                      'new-trip-button'
                    )}
                  >
                    Démarrer une nouvelle assurance voyage ✈️
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        {countryChangeSubscriptionId ? (
          <CountryChange
            countries={countries}
            subscriptionId={countryChangeSubscriptionId}
            onClose={onClose}
            onCountryChange={handleInProgressCountryChange}
          />
        ) : null}
        {toCancel ? (
          <CancelSubscription
            subscription={toCancel}
            onClose={() => setToCancel(false)}
            onCanceled={removeSubscription}
          />
        ) : null}
      </MainContainer>
      <SupportForm />
      <Footer />
      <style jsx>{componentStyles}</style>
      {subscriptionsStyles.styles}
      {buttonStyles.styles}
    </>
  );
};

Index.getInitialProps = async ({ req, res }) => {
  const baseUrl = baseUrlOf(req);
  const opts = fetchOpts(req, res);

  const [profile, subscriptions] = await Promise.all([
    user.profile(req, res),
    subscriptionApi.get(baseUrl, opts)
  ]);

  if (profile && subscriptions) {
    return { profile, subscriptions };
  } else {
    redirect(res, '/login');
  }
};

export default Index;
export { HREF, PROFILE_HREF };
